import React, { useEffect, useState } from 'react';
import { Button, Form, Nav } from 'react-bootstrap';
import Dropzone from 'react-dropzone';
import ModalComponent from '../../../../helpers_components/ModalComponent';
import { NotificationManager } from 'react-notifications';
import useElectronicSignatureManager from '../hooks/useElectronicSignatureManager';
import CredentialsConfirmationModal from '../CredentialsConfirmationModal';

const CredentialsModal = ({
  showModalConfirmation,
  isModalSowing,
  onCloseModal,
  addElectronicSignatureHandle,
  isMultipleSignature,
  onCreatePkcs7,
  isReadyToCreatePkcs7,
  disableSignatureButton,
}) => {
  const [electronicSignatureErrorText, setElectronicSignatureErrorText] = useState('');
  const [isModalConfirmationShowing, setIsModalConfirmationShowing] = useState(false);

  const {
    formElectronicSignatureValues,
    onSetElectronicSignatureKeyFile,
    onAddElectronicSignature,
    onFormElectronicSignatureChange,
    electroniSignatures,
    resetElectronicSignatureValues,
  } = useElectronicSignatureManager({ isMultipleSignature });

  const handleResetElectronicSignatureErrorText = () => {
    if (Boolean(electronicSignatureErrorText)) {
      setElectronicSignatureErrorText('');
    }
  };

  useEffect(() => {
    handleResetElectronicSignatureErrorText();
  }, [formElectronicSignatureValues]);

  const onAddElectronicSignatureHandler = async () => {
    try {
      await onAddElectronicSignature(addElectronicSignatureHandle);

      if (showModalConfirmation) {
        setIsModalConfirmationShowing(true);
      } else {
        onCloseModal();
      }
    } catch (error) {
      if (error.message === 'Invalid credentials') {
        NotificationManager.error(
          'Existen errores que te impiden continuar, por favor revisa los campos marcados con rojo'
        );
        setElectronicSignatureErrorText('Credenciales de firma electrónica inválidas');
      }
    }
  };

  const footerModal = (
    <>
      <Button variant="secondary" onClick={onCloseModal}>
        Cancelar
      </Button>

      <Button onClick={onAddElectronicSignatureHandler}>Agregar</Button>
    </>
  );

  return (
    <>
      {isModalSowing && (
        <ModalComponent
          header="Firma Electronica"
          body={
            <CredentialsBodyModal
              onFormElectronicSignatureChange={onFormElectronicSignatureChange}
              formElectronicSignatureValues={formElectronicSignatureValues}
              onSetElectronicSignatureKeyFile={onSetElectronicSignatureKeyFile}
              electronicSignatureErrorText={electronicSignatureErrorText}
              electroniSignatures={electroniSignatures}
              isMultipleSignature={isMultipleSignature}
            />
          }
          footer={footerModal}
          show={isModalSowing}
          canClose
          onHide={onCloseModal}
        />
      )}
      {isModalConfirmationShowing && (
        <CredentialsConfirmationModal
          isModalShowing={isModalConfirmationShowing}
          onCloseModal={() => {
            setIsModalConfirmationShowing(false);
            resetElectronicSignatureValues(isMultipleSignature);
          }}
          isReadyToCreatePkcs7={isReadyToCreatePkcs7}
          onCreatePkcs7={onCreatePkcs7}
          disableSignatureButton={disableSignatureButton}
        />
      )}
    </>
  );
};

export const CredentialsBodyModal = ({
  onFormElectronicSignatureChange,
  formElectronicSignatureValues,
  electronicSignatureErrorText,
  onSetElectronicSignatureKeyFile,
  electroniSignatures,
  isMultipleSignature,
}) => {
  const satFiles = (
    <>
      <SignatureInputFile
        label="* Llave privada (.cer)"
        setFile={(file) => onSetElectronicSignatureKeyFile(file, 'cer')}
        id={`cer`}
        accept=".cer"
        fileName={formElectronicSignatureValues.cer ? formElectronicSignatureValues.cer.name : ''}
      />

      <SignatureInputFile
        label="* Llave privada (.key)"
        setFile={(file) => onSetElectronicSignatureKeyFile(file, 'key')}
        id={`key`}
        accept=".key"
        fileName={formElectronicSignatureValues.key ? formElectronicSignatureValues.key.name : ''}
      />
    </>
  );

  const firelFiles = (
    <div>
      <SignatureInputFile
        label="* Llave privada (.pfx)"
        setFile={(file) => onSetElectronicSignatureKeyFile(file, 'pfx')}
        id={`pfx`}
        accept=".pfx, .p12"
        fileName={formElectronicSignatureValues.pfx ? formElectronicSignatureValues.pfx.name : ''}
      />
    </div>
  );

  return (
    <div className="border-bottom border-secondary mb-3 pb-3">
      <p className="m-0">* Firma Electrónica</p>
      <Nav
        className="mb-3"
        variant="tabs"
        defaultActiveKey={formElectronicSignatureValues.signaturetype}
        activeKey={formElectronicSignatureValues.signaturetype}
      >
        {electroniSignatures.getTypes(isMultipleSignature).map((signature) => (
          <Nav.Item key={signature.id}>
            <Nav.Link
              onClick={onFormElectronicSignatureChange}
              eventKey={signature.type}
              name={'signaturetype'}
            >
              Firma {signature.displayName}
            </Nav.Link>
          </Nav.Item>
        ))}
      </Nav>

      <div className="mb-3">
        {formElectronicSignatureValues.signaturetype === 'sat' ? satFiles : firelFiles}
      </div>
      <Form.Group className="mb-3" controlId="formGroupPasswordFiel">
        <Form.Label>* Contraseña</Form.Label>
        <Form.Control
          type="password"
          placeholder="Password"
          onChange={onFormElectronicSignatureChange}
          value={formElectronicSignatureValues.password}
          name="password"
          autoComplete="off"
        />
        <Form.Text className="text-muted">
          Tu contraseña no se guardará en ningún momento, sólo se usará para obtener tu Fiel.
        </Form.Text>
      </Form.Group>
      {Boolean(electronicSignatureErrorText) && (
        <div className="text-danger mt-3">{electronicSignatureErrorText}</div>
      )}
    </div>
  );
};

export const SignatureInputFile = (props) => {
  const { setFile, label, id, fileName, isMultiFiles = false, fileData, ...restOfProps } = props;
  const onDrop = (files) => {
    let file = isMultiFiles ? files : files[0];
    setFile(file);
  };

  return (
    <div className="margin-top-2em">
      <Form.Group controlId="formBasicFile">
        <Form.Label>{label}</Form.Label>
        <div className="d-flex align-items-center">
          <Dropzone onDrop={onDrop}>
            {({ getRootProps, getInputProps, isDragActive }) => (
              <section>
                <div
                  {...getRootProps()}
                  style={{
                    border: '2px dashed #007bff',
                    borderRadius: '8px',
                    padding: '16px',
                    background: isDragActive ? '#e9f5ff' : '#f8f9fa',
                    cursor: 'pointer',
                    minWidth: '200px',
                    minHeight: '100px',
                    textAlign: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    transition: 'background 0.2s',
                  }}
                >
                  <input id={id} name={id} {...getInputProps()} {...restOfProps} />
                  {fileData && fileData.icon && (
                    <img
                      src={fileData.icon}
                      alt=""
                      style={{ width: '30px', height: '30px', marginBottom: '10px' }}
                    />
                  )}
                  {fileName && (
                    <div className="ml-2 mb-2 p-3 bg-success text-white rounded text-break">
                      {fileName}
                    </div>
                  )}
                  <div style={{ fontSize: '0.9em', color: '#6c757d' }}>
                    {isDragActive
                      ? 'Suelta el archivo aquí...'
                      : 'Arrastra y suelta el archivo aquí, o haz clic para seleccionar'}
                  </div>
                </div>
              </section>
            )}
          </Dropzone>
        </div>
      </Form.Group>
    </div>
  );
};

export default CredentialsModal;
